export type ChannelsOverride = 'off' | 'empty' | 'many'
export type MembersOverride = 'off' | 'many'
export type PlanOverride = 'off' | 'free' | 'essentials' | 'team' | 'agency'

export type Service =
  | 'bluesky'
  | 'facebook'
  | 'googlebusiness'
  | 'instagram'
  | 'linkedin'
  | 'mastodon'
  | 'pinterest'
  | 'startPage'
  | 'threads'
  | 'tiktok'
  | 'twitter'
  | 'youtube'

export const SERVICE_OPTIONS: { value: Service; label: string }[] = [
  { value: 'bluesky', label: 'Bluesky' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'googlebusiness', label: 'Google Business' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'mastodon', label: 'Mastodon' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'startPage', label: 'Start Page' },
  { value: 'threads', label: 'Threads' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'youtube', label: 'YouTube' },
]

// Mirrors src/helpers/post.ts's postStatusByTab keys/statuses — duplicated rather than imported,
// since this module is statically bundled into the main chunk and shouldn't pull in the wider app
// helper tree just for a 4-entry lookup table.
export type PostTabKey = 'queue' | 'drafts' | 'sent' | 'approvals'

export const POST_TAB_OPTIONS: { value: PostTabKey; label: string }[] = [
  { value: 'queue', label: 'Queue' },
  { value: 'drafts', label: 'Drafts' },
  { value: 'sent', label: 'Sent' },
  { value: 'approvals', label: 'Approvals' },
]

export const POST_TAB_STATUSES: Record<PostTabKey, string[]> = {
  queue: ['scheduled', 'error'],
  drafts: ['draft'],
  sent: ['sent', 'sending'],
  approvals: ['needs_approval'],
}

export const POST_TAB_REPRESENTATIVE_STATUS: Record<PostTabKey, string> = {
  queue: 'scheduled',
  drafts: 'draft',
  sent: 'sent',
  approvals: 'needs_approval',
}

export type PostCountsByTab = Record<PostTabKey, number>

export const EMPTY_POST_COUNTS: PostCountsByTab = {
  queue: 0,
  drafts: 0,
  sent: 0,
  approvals: 0,
}

export const DEFAULT_POSTS_PER_CHANNEL = 2

export interface MocktailChannelSpec {
  id: string
  service: Service
  postCounts: PostCountsByTab
  // All optional — left blank, the walker falls back to an auto-generated name/handle and
  // whichever real channel's own avatar it's cloning the rest of the shape from.
  name?: string
  handle?: string
  avatarUrl?: string
}

export interface MocktailScenario {
  channels: ChannelsOverride
  channelSpecs: MocktailChannelSpec[]
  members: MembersOverride
  membersCount: number
  plan: PlanOverride
}

export const DEFAULT_SCENARIO: MocktailScenario = {
  channels: 'off',
  channelSpecs: [
    {
      id: 'default',
      service: 'instagram',
      postCounts: { ...EMPTY_POST_COUNTS, queue: DEFAULT_POSTS_PER_CHANNEL },
    },
  ],
  members: 'off',
  membersCount: 12,
  plan: 'off',
}

// Semi-even, not identical — random per-channel weights scaled to the requested total, so
// "distribute randomly" doesn't just divide evenly every time. Plain Math.random rather than
// faker: this only needs numeric jitter, and scenario.ts is statically bundled (unlike the
// dynamically-imported engine/ directory), so it can't afford to pull faker into the main chunk.
export function distributePostsRandomly(
  total: number,
  count: number,
): number[] {
  if (count <= 0) return []
  if (total <= 0) return Array.from({ length: count }, () => 0)

  const weights = Array.from({ length: count }, () => 0.5 + Math.random())
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0)
  const shares = weights.map((weight) =>
    Math.floor((weight / weightSum) * total),
  )
  let remainder = total - shares.reduce((sum, share) => sum + share, 0)

  const shuffledIndices = shares.map((_, index) => index)
  for (let i = shuffledIndices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const swap = shuffledIndices[i]
    shuffledIndices[i] = shuffledIndices[j]
    shuffledIndices[j] = swap
  }
  for (const index of shuffledIndices) {
    if (remainder <= 0) break
    shares[index] += 1
    remainder -= 1
  }

  return shares
}

export const PLAN_TIER_OPTIONS: { value: PlanOverride; label: string }[] = [
  { value: 'free', label: 'Free' },
  { value: 'essentials', label: 'Essentials' },
  { value: 'team', label: 'Team' },
  { value: 'agency', label: 'Agency' },
]

export function isChannelsOverride(value: string): value is ChannelsOverride {
  return value === 'off' || value === 'empty' || value === 'many'
}

export function isMembersOverride(value: string): value is MembersOverride {
  return value === 'off' || value === 'many'
}

export function isPlanOverride(value: string): value is PlanOverride {
  return (
    value === 'off' ||
    value === 'free' ||
    value === 'essentials' ||
    value === 'team' ||
    value === 'agency'
  )
}

export function isService(value: string): value is Service {
  return SERVICE_OPTIONS.some((option) => option.value === value)
}

export function isScenarioActive(scenario: MocktailScenario): boolean {
  return (
    scenario.channels !== 'off' ||
    scenario.members !== 'off' ||
    scenario.plan !== 'off'
  )
}

export function totalPostsForSpec(spec: MocktailChannelSpec): number {
  return POST_TAB_OPTIONS.reduce(
    (sum, option) => sum + spec.postCounts[option.value],
    0,
  )
}

export function describeScenario(scenario: MocktailScenario): string[] {
  const labels: string[] = []

  if (scenario.channels === 'empty') labels.push('No channels')
  if (scenario.channels === 'many') {
    const totalPosts = scenario.channelSpecs.reduce(
      (sum, spec) => sum + totalPostsForSpec(spec),
      0,
    )
    labels.push(`${scenario.channelSpecs.length} channels`)
    if (totalPosts > 0) labels.push(`${totalPosts} posts`)
  }
  if (scenario.members === 'many') {
    labels.push(`${scenario.membersCount} members`)
  }
  if (scenario.plan !== 'off') {
    const tier = PLAN_TIER_OPTIONS.find(
      (option) => option.value === scenario.plan,
    )
    if (tier) labels.push(`${tier.label} plan`)
  }

  return labels
}
